import {
    Box,
    Button,
    Dialog,
    DialogContent,
    Grid,
    Typography,
} from '@mui/material'
import AppDialogTitle from 'components/Dialog/AppDialogTitle'
import Loading from 'components/Loading/Loading'
import MoneyInput from 'components/NumberInput/MoneyInput/MoneyInput'
import { SALES_TOP_UP_GIFT_CARD } from 'constant/permissions'
import usePermission from 'hooks/usePermission/usePermission'
import { CustomerLookupContract } from 'models'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormat } from 'Services/localize/format'
import { PAYMENT_TYPE_ID } from 'constant/paymentType'
import { CalculatorDialog } from '../CalculationInput/CalculatorDialog'
import TopUpReceiptDialog from './TopUpReceiptDialog'
import { OnSuccessfulTopupCallback, TopUpGiftCardInfo } from './types'
import useTopupGiftCard from './useTopupGiftCard'
import { usePosPaymentSettingContext } from 'views/Sale/contexts/PosPaymentSettingContext'
import { PaymentDialog } from '../payment/PaymentDialog'
import { useSendOrderToCFD } from 'hooks/useSendMessageToCFD/useSendOrderToCFD'
import { usePosContext } from 'views/Sale/contexts/PosProvider'
import CloseDialogButton from 'components/Dialog/CloseDialogButton'

interface Props {
    open: boolean
    customerID?: string
    topUpGiftCard?: TopUpGiftCardInfo
    customers: CustomerLookupContract[]
    onSuccessfulTopup: OnSuccessfulTopupCallback
    onDismiss: () => void
}

function TopupGiftCardPopup({
    open,
    customers,
    topUpGiftCard,
    onSuccessfulTopup,
    onDismiss,
    customerID,
}: Props) {
    const { isOffline } = usePosContext()
    const { t } = useTranslation()
    const { currency } = useFormat()

    const { paymentSettings, tipsSetting } = usePosPaymentSettingContext()
    const enabledPaymentSettings = useMemo(
        () =>
            paymentSettings?.filter(
                (p) =>
                    p.paymentTypeID !== PAYMENT_TYPE_ID.GIFTCARD &&
                    p.paymentTypeID !== PAYMENT_TYPE_ID.CHANNEL_ACCOUNT,
            ),
        [paymentSettings],
    )

    const [inputOpen, setInputOpen] = useState(false)
    const {
        loading,
        loadDependencies,
        giftCardPaymentSettings,
        topUpAmount,
        onChangeTopUpAmount,
        onRequestPayment,
        onProcessingTopUpPayment,
        openPaymentDialog,
        currentTopUpResult,
        onClosedReceiptDialog,
        onResetValue,
        handleGetSaleDetails,
        saleDetail,
        onFinishSale,
    } = useTopupGiftCard({
        topUpGiftCard,
        onSuccessfulTopup,
    })

    useEffect(() => {
        if (open) loadDependencies()
    }, [open, loadDependencies])
    const { hasPermissionWithOther } = usePermission()
    const hasTopupPermission = hasPermissionWithOther([SALES_TOP_UP_GIFT_CARD])
    const onClose = useCallback(() => {
        onResetValue()
        onDismiss()
    }, [onDismiss, onResetValue])
    const { resetOrderInCFD } = useSendOrderToCFD()

    const onClosePaymentDialog = useCallback(() => {
        onRequestPayment(false)
        resetOrderInCFD()
    }, [onRequestPayment, resetOrderInCFD])
    return (
        <>
            <Dialog
                open={hasTopupPermission && open}
                onClose={onClose}
                fullWidth
                maxWidth="md"
            >
                <AppDialogTitle
                    text={t('quick_sell.gift_card_top_up.label.dialog_title')}
                />
                {!loading && (
                    <CloseDialogButton
                        dialogName="top up gift card pop up"
                        onClick={onClose}
                    />
                )}
                <DialogContent>
                    {loading ? (
                        <Loading />
                    ) : (
                        <>
                            <Typography fontWeight="bold" pb={2}>
                                {t(
                                    'quick_sell.gift_card_top_up.label.top_up_amount',
                                )}
                            </Typography>
                            <Grid container spacing={2} pb={2}>
                                {giftCardPaymentSettings?.quickTopUpAmounts?.map(
                                    (amount) => (
                                        <Grid item xs key={'amount_' + amount}>
                                            <Button
                                                size="large"
                                                fullWidth
                                                variant="outlined"
                                                onClick={() =>
                                                    onChangeTopUpAmount(amount)
                                                }
                                            >
                                                {currency(amount)}
                                            </Button>
                                        </Grid>
                                    ),
                                )}
                            </Grid>
                            <Box sx={{ mt: 1 }}>
                                <MoneyInput
                                    fullWidth
                                    value={topUpAmount}
                                    onChange={(e: any) =>
                                        onChangeTopUpAmount(
                                            Number(e.target.value),
                                        )
                                    }
                                    onClick={() => setInputOpen(true)}
                                />
                            </Box>
                            <Grid container justifyContent="end" sx={{ mt: 2 }}>
                                <Button
                                    color="primary"
                                    onClick={() => onRequestPayment(true)}
                                >
                                    {t(
                                        'quick_sell.gift_card_top_up.button.proceed',
                                    )}
                                </Button>
                            </Grid>
                        </>
                    )}
                </DialogContent>
            </Dialog>
            {openPaymentDialog && (
                <PaymentDialog
                    isOffline={isOffline}
                    paymentSettings={enabledPaymentSettings}
                    orderCustomerID={customerID}
                    customerList={customers}
                    totalDueAmount={topUpAmount}
                    onCloseDialog={onClosePaymentDialog}
                    open={openPaymentDialog}
                    tipsSetting={tipsSetting}
                    initPayments={[]}
                    useSplitPayment={false}
                    useTip={false}
                    onProcessingSale={onProcessingTopUpPayment}
                    getSaleDetailByID={handleGetSaleDetails}
                    orderDetail={saleDetail}
                    onFinishSalePayment={onFinishSale}
                />
            )}
            <CalculatorDialog
                quickButtons={giftCardPaymentSettings?.quickTopUpAmounts ?? []}
                initAmount={topUpAmount}
                open={inputOpen}
                onClose={() => setInputOpen(false)}
                onConfirm={(value) => {
                    onChangeTopUpAmount(value.toNumber())
                    setInputOpen(false)
                }}
            />
            {currentTopUpResult && (
                <TopUpReceiptDialog
                    data={currentTopUpResult}
                    onClose={onClosedReceiptDialog}
                />
            )}
        </>
    )
}

export default TopupGiftCardPopup
